import {
  createPublicOrder,
  requestOrderCreateToken,
} from "api/repository/orders.api";
import {
  DEFAULT_CHECKOUT_ORDER_DEFAULTS,
  DEFAULT_WHATSAPP_PHONE_NUMBER,
} from "config/clientConfig";

export const CHECKOUT_PROVIDERS = Object.freeze({
  WHATSAPP: "whatsapp",
  API: "api",
});

const DEFAULT_CHECKOUT_VALUES = Object.freeze({
  provider: CHECKOUT_PROVIDERS.WHATSAPP,
  whatsappPhoneNumber: DEFAULT_WHATSAPP_PHONE_NUMBER,
  orderType: DEFAULT_CHECKOUT_ORDER_DEFAULTS.orderType,
  shippingStatus: DEFAULT_CHECKOUT_ORDER_DEFAULTS.shippingStatus,
  paymentStatus: DEFAULT_CHECKOUT_ORDER_DEFAULTS.paymentStatus,
  phoneExtensionDefault: DEFAULT_CHECKOUT_ORDER_DEFAULTS.phoneExtensionDefault,
});

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeText = (value) => String(value ?? "").trim();
export const normalizeWhatsAppPhone = (value) =>
  normalizeText(value).replace(/\D/g, "");

export const normalizePhoneExtension = (value) =>
  normalizeText(value).replace(/\D/g, "").slice(0, 3);

const getCartQuantity = (item) =>
  Math.max(0, safeNumber(item?.cartQuantity ?? item?.quantity ?? 0));

const getUnitPrice = (item) =>
  Math.max(0, safeNumber(item?.selling_price ?? item?.price ?? 0));

const normalizeCheckoutConfig = (checkoutConfig = {}) => ({
  provider: normalizeText(checkoutConfig?.provider).toLowerCase() ||
    DEFAULT_CHECKOUT_VALUES.provider,
  whatsappPhoneNumber:
    normalizeWhatsAppPhone(checkoutConfig?.whatsapp?.phoneNumber) ||
    normalizeWhatsAppPhone(DEFAULT_CHECKOUT_VALUES.whatsappPhoneNumber),
  orderType:
    normalizeText(checkoutConfig?.defaults?.orderType) ||
    DEFAULT_CHECKOUT_VALUES.orderType,
  shippingStatus:
    normalizeText(checkoutConfig?.defaults?.shippingStatus) ||
    DEFAULT_CHECKOUT_VALUES.shippingStatus,
  paymentStatus:
    normalizeText(checkoutConfig?.defaults?.paymentStatus) ||
    DEFAULT_CHECKOUT_VALUES.paymentStatus,
  phoneExtensionDefault:
    normalizePhoneExtension(checkoutConfig?.defaults?.phoneExtensionDefault) ||
    DEFAULT_CHECKOUT_VALUES.phoneExtensionDefault,
});

export const normalizePhoneNumber = (value) =>
  normalizeText(value).replace(/[\s\-()]/g, "");

export const isValidPhoneNumber = (value) => {
  const normalized = normalizePhoneNumber(value);
  if (!normalized) return false;
  return /^\+?\d{7,15}$/.test(normalized);
};

export const validateCheckoutCustomer = (customer = {}) => {
  const errors = {};
  const normalizedExtension = normalizePhoneExtension(
    customer?.customerPhoneExtension
  );

  if (!normalizeText(customer?.customerName)) {
    errors.customerName = "Customer name is required.";
  }

  if (!isValidPhoneNumber(customer?.customerPhone)) {
    errors.customerPhone =
      "Customer phone must contain 7 to 15 digits (optional leading +).";
  }

  if (
    normalizeText(customer?.customerPhoneExtension) &&
    !/^\d{1,3}$/.test(normalizedExtension)
  ) {
    errors.customerPhoneExtension =
      "Extension must be numeric and up to 3 digits.";
  }

  return errors;
};

const createNormalizedItems = (cart = []) =>
  cart
    .map((item) => {
      const quantity = getCartQuantity(item);
      if (quantity <= 0) return null;

      const sellingPrice = getUnitPrice(item);
      const originalSellingPrice = Math.max(
        0,
        safeNumber(item?.originalSellingPrice ?? item?.selling_price ?? item?.price ?? 0)
      );
      const itemDiscount = Math.max(0, safeNumber(item?.discount ?? 0));

      return {
        productId: item?.productId ?? item?.id ?? null,
        name: normalizeText(item?.name),
        selectedSize: normalizeText(item?.selectedSize),
        selectedColor: normalizeText(item?.selectedColor),
        qty: quantity,
        sellingPrice,
        originalSellingPrice,
        costPrice: Math.max(0, safeNumber(item?.costPrice ?? item?.cost_price ?? 0)),
        discount: itemDiscount,
        isForStuff: safeNumber(item?.isForStuff ?? 0),
        ingredients: Array.isArray(item?.ingredients) ? item.ingredients : [],
        lineTotal: sellingPrice * quantity,
      };
    })
    .filter(Boolean);

export const buildOrderDraft = (
  cart = [],
  customer = {},
  checkoutConfig = {},
  deliveryCost = 0
) => {
  const resolvedConfig = normalizeCheckoutConfig(checkoutConfig);
  const items = createNormalizedItems(cart);
  const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
  const discount = 0;
  const total = Math.max(0, subtotal - discount);
  const paidAmount = 0;
  const remainingAmount = Math.max(0, total - paidAmount);
  const normalizedDeliveryCost = Math.max(0, safeNumber(deliveryCost));

  return {
    provider: resolvedConfig.provider,
    customer: {
      customerName: normalizeText(customer?.customerName),
      customerPhone: normalizePhoneNumber(customer?.customerPhone),
      customerPhoneExtension:
        normalizePhoneExtension(customer?.customerPhoneExtension) ||
        resolvedConfig.phoneExtensionDefault,
      address: normalizeText(customer?.address),
      villageId: normalizeText(customer?.villageId),
      areaId: normalizeText(customer?.areaId),
      notes: normalizeText(customer?.notes),
    },
    defaults: {
      orderType: resolvedConfig.orderType,
      shippingStatus: resolvedConfig.shippingStatus,
      paymentStatus: resolvedConfig.paymentStatus,
    },
    items,
    subtotal,
    discount,
    total,
    deliveryCost: normalizedDeliveryCost,
    paidAmount,
    remainingAmount,
    whatsappPhoneNumber: resolvedConfig.whatsappPhoneNumber,
  };
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(safeNumber(value));

export const buildPublicOrderPayload = (orderDraft) => {
  const villageId = safeNumber(orderDraft?.customer?.villageId);
  const areaId = safeNumber(orderDraft?.customer?.areaId);

  return {
    customerPhone: orderDraft?.customer?.customerPhone || "",
    customerPhoneExtension:
      normalizePhoneExtension(orderDraft?.customer?.customerPhoneExtension) ||
      DEFAULT_CHECKOUT_VALUES.phoneExtensionDefault,
    customerName: orderDraft?.customer?.customerName || "",
    orderType: orderDraft?.defaults?.orderType || DEFAULT_CHECKOUT_VALUES.orderType,
    notes: normalizeText(orderDraft?.customer?.notes),
    address: normalizeText(orderDraft?.customer?.address),
    uVillageId: villageId > 0 ? villageId : null,
    areaId: areaId > 0 ? areaId : null,
    subtotal: safeNumber(orderDraft?.subtotal),
    discount: safeNumber(orderDraft?.discount),
    total: safeNumber(orderDraft?.total),
    remainingAmount: safeNumber(orderDraft?.remainingAmount),
    items: Array.isArray(orderDraft?.items)
      ? orderDraft.items.map((item) => ({
          productId: item?.productId,
          qty: safeNumber(item?.qty),
          sellingPrice: safeNumber(item?.sellingPrice),
          originalSellingPrice: safeNumber(item?.originalSellingPrice),
          discount: safeNumber(item?.discount),
        }))
      : [],
  };
};

export const buildWhatsAppMessage = (orderDraft, language = "en") => {
  const isArabic = language === "ar";
  const labels = isArabic
    ? {
        title: "طلب جديد",
        customer: "الزبون",
        phone: "الهاتف",
        address: "العنوان",
        notes: "ملاحظات",
        items: "المنتجات",
        total: "المجموع",
        delivery: "التوصيل",
        qty: "الكمية",
        price: "السعر",
        size: "المقاس",
        color: "اللون",
      }
    : {
        title: "New Order",
        customer: "Customer",
        phone: "Phone",
        address: "Address",
        notes: "Notes",
        items: "Items",
        total: "Total",
        delivery: "Delivery",
        qty: "Qty",
        price: "Price",
        size: "Size",
        color: "Color",
      };

  const lines = [];
  lines.push(`${labels.title}`);
  lines.push(`${labels.customer}: ${orderDraft?.customer?.customerName || "-"}`);
  lines.push(
    `${labels.phone} : ${orderDraft?.customer?.customerPhoneExtension || ""}${
      orderDraft?.customer?.customerPhone || "-"
    }`.trim()
  );

  if (orderDraft?.customer?.address) {
    lines.push(`${labels.address}: ${orderDraft.customer.address}`);
  }
  if (orderDraft?.customer?.notes) {
    lines.push(`${labels.notes}: ${orderDraft.customer.notes}`);
  }

  lines.push("");
  lines.push(`${labels.items}:`);

  (orderDraft?.items || []).forEach((item, index) => {
    const details = [];
    if (item.selectedSize) details.push(`${labels.size}: ${item.selectedSize}`);
    if (item.selectedColor) details.push(`${labels.color}: ${item.selectedColor}`);
    const detailsText = details.length > 0 ? ` (${details.join(", ")})` : "";

    lines.push(
      `${index + 1}. ${item.name || "Item"}${detailsText} - ${labels.qty}: ${
        item.qty
      } - ${labels.price}: ${formatCurrency(item.sellingPrice)}`
    );
  });

  lines.push("");
  if (orderDraft?.deliveryCost > 0) {
    lines.push(
      `${labels.delivery}: ${formatCurrency(orderDraft.deliveryCost)}`
    );
  }
  lines.push(
    `${labels.total}: ${formatCurrency(
      safeNumber(orderDraft?.total) + safeNumber(orderDraft?.deliveryCost)
    )}`
  );

  return lines.join("\n");
};

export const buildWhatsAppUrl = (phoneNumber, message) => {
  const normalizedPhone = normalizePhoneNumber(phoneNumber).replace(/^\+/, "");
  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
};

const openWhatsAppOrder = (orderDraft, language, openWindow) => {
  if (!orderDraft?.whatsappPhoneNumber) {
    throw new Error("WhatsApp checkout is not configured: missing phone number.");
  }

  const message = buildWhatsAppMessage(orderDraft, language);
  const url = buildWhatsAppUrl(orderDraft.whatsappPhoneNumber, message);
  const openFn =
    openWindow ||
    ((targetUrl) => window.open(targetUrl, "_blank", "noopener,noreferrer"));
  const openedWindow = openFn(url);

  if (openedWindow === null) {
    throw new Error("Unable to open WhatsApp. Please allow popups and try again.");
  }

  return {
    provider: CHECKOUT_PROVIDERS.WHATSAPP,
    whatsappUrl: url,
  };
};

const getResponseData = (response) => response?.data ?? response;

const submitApiOrder = async (orderDraft) => {
  const invalidItem = orderDraft?.items?.find(
    (item) => !item?.productId || safeNumber(item?.qty) <= 0
  );
  if (invalidItem) {
    throw new Error("Each order item must include productId and a positive qty.");
  }

  const tokenResponse = await requestOrderCreateToken();
  const tokenData = getResponseData(tokenResponse);
  if (!tokenData?.accessToken) {
    throw new Error("Order token response is missing accessToken.");
  }

  const payload = buildPublicOrderPayload(orderDraft);
  const response = await createPublicOrder(payload, tokenData.accessToken);
  const orderData = getResponseData(response);
  if (
    !orderData?.orderId ||
    !orderData?.trackingToken ||
    !orderData?.trackingTokenExpiresAt
  ) {
    throw new Error("Order response is missing tracking information.");
  }

  return {
    provider: CHECKOUT_PROVIDERS.API,
    payload,
    response,
    orderId: orderData.orderId,
    trackingToken: orderData.trackingToken,
    trackingTokenExpiresAt: orderData.trackingTokenExpiresAt,
  };
};

export const submitCheckout = async ({
  cart = [],
  customer = {},
  language = "en",
  checkoutConfig = {},
  deliveryCost = 0,
  linkedWithUltra = false,
  openWindow,
}) => {
  const orderDraft = buildOrderDraft(cart, customer, checkoutConfig, deliveryCost);

  if (!orderDraft.items.length) {
    throw new Error("Cart is empty.");
  }

  const customerErrors = validateCheckoutCustomer(orderDraft.customer);
  if (Object.keys(customerErrors).length > 0) {
    throw new Error("Customer information is invalid.");
  }

  if (linkedWithUltra && !orderDraft?.customer?.villageId) {
    throw new Error("Please select a village from the dropdown.");
  }

  if (orderDraft.provider === CHECKOUT_PROVIDERS.WHATSAPP) {
    const result = openWhatsAppOrder(orderDraft, language, openWindow);
    return { ...result, orderDraft };
  }

  if (orderDraft.provider === CHECKOUT_PROVIDERS.API) {
    const result = await submitApiOrder(orderDraft);
    return { ...result, orderDraft };
  }

  throw new Error(
    `Unsupported checkout provider "${orderDraft.provider}". Use "whatsapp" or "api".`
  );
};
